/**
 * Flexible field accessor for TrustCallAdvancedInsightDto.
 *
 * The WebAPI may serialize property names in PascalCase (default System.Text.Json
 * with no naming policy) or camelCase (if a policy is configured). This helper
 * tries multiple casing variants so the frontend works regardless.
 *
 * System.Text.Json CamelCase transforms:
 *   TNRMScore → tnrmScore (leading all-caps lowered until last before lowercase)
 *   CrowdSourceJSON → crowdSourceJSON (first char lowered; trailing all-caps stays)
 *   ReputationHistoryJson → reputationHistoryJson (standard camelCase)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function field(obj: Record<string, any>, pascalName: string): any {
  if (obj == null) return undefined;

  // 1. Exact PascalCase: "TNRMScore", "CrowdSourceJSON"
  if (obj[pascalName] !== undefined) return obj[pascalName];

  // 2. Simple camelCase (lowercase first char): "tNRMScore", "crowdSourceJSON"
  const camel1 = pascalName.charAt(0).toLowerCase() + pascalName.slice(1);
  if (obj[camel1] !== undefined) return obj[camel1];

  // 3. System.Text.Json camelCase: lowercase leading uppercase run
  //    "TNRMScore" → "tnrmScore", "TNRMScoreRetrievedOn" → "tnrmScoreRetrievedOn"
  const sysTextJson = pascalName.replace(/^([A-Z]+)([A-Z][a-z])/, (_m, leading, next) =>
    leading.toLowerCase() + next
  ).replace(/^([A-Z]+)$/, (m) => m.toLowerCase());
  // Also handle single leading uppercase: "CallVolJson" → "callVolJson"
  const sysTextJson2 = pascalName.charAt(0).toLowerCase() + pascalName.slice(1);
  if (sysTextJson !== pascalName && obj[sysTextJson] !== undefined) return obj[sysTextJson];
  if (sysTextJson2 !== sysTextJson && obj[sysTextJson2] !== undefined) return obj[sysTextJson2];

  // 4. Lowered trailing acronym: "CrowdSourceJSON" → "crowdSourceJson"
  const lowerTrailing = camel1.replace(/([A-Z]{2,})$/g, (m) => m.charAt(0) + m.slice(1).toLowerCase());
  if (lowerTrailing !== camel1 && obj[lowerTrailing] !== undefined) return obj[lowerTrailing];

  // 5. Fully lowered leading + trailing: "TNRMScoreRetrievedOn" → "tnrmscoreretrievedon" (unlikely but safe)
  const lower = pascalName.toLowerCase();
  // Check all keys case-insensitively as last resort
  for (const key of Object.keys(obj)) {
    if (key.toLowerCase() === lower) return obj[key];
  }

  return undefined;
}
